import { Injectable, signal } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
}

interface ToastTimer {
  timeoutId: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastrService {
  toasts = signal<Toast[]>([]);

  private nextId = 0;
  private timers = new Map<number, ToastTimer>();

  show(message: string, type: ToastType = 'info', duration = 4000): void {
    const id = ++this.nextId;
    this.toasts.update(list => [...list, { id, type, message, duration }]);

    if (duration > 0) {
      this.timers.set(id, { timeoutId: setTimeout(() => this.dismiss(id), duration), remaining: duration, startedAt: Date.now() });
    }
  }

  success(message: string, duration = 4000): void {
    this.show(message, 'success', duration);
  }

  error(message: string, duration = 5000): void {
    this.show(message, 'error', duration);
  }

  info(message: string, duration = 4000): void {
    this.show(message, 'info', duration);
  }

  warning(message: string, duration = 4500): void {
    this.show(message, 'warning', duration);
  }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.timeoutId) {
      clearTimeout(timer.timeoutId);
    }
    this.timers.delete(id);
    this.toasts.update(list => list.filter(t => t.id !== id));
  }

  pause(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || !timer.timeoutId) return;

    clearTimeout(timer.timeoutId);
    timer.remaining -= Date.now() - timer.startedAt;
    timer.timeoutId = null;
  }

  resume(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.timeoutId || timer.remaining <= 0) return;

    timer.startedAt = Date.now();
    timer.timeoutId = setTimeout(() => this.dismiss(id), timer.remaining);
  }
}
